import { Button, Group, Paper, Stack, Text } from '@mantine/core';
import { IconArrowBackUp } from '@tabler/icons-react';
import { DeletedDaySummary } from '../../types/mealPlan';

interface DeletedDayCardProps {
  day: DeletedDaySummary;
  restoring: boolean;
  disabled: boolean;
  onRestore: () => void;
  onPickDifferent: () => void;
  onAddCustomMeal: () => void;
}

// Placeholder left in a removed day's grid position so the day can be brought
// back after the undo toast is gone. "Pick a different meal" restores the old
// meal first, then opens the regular swap flow on it.
// Dinner keeps the original whole-day wording; breakfast and lunch name the
// meal, since their dates still hold the other slots.
const removedNoun = (day: DeletedDaySummary) => {
  const mealType = day.mealType ?? 'dinner';
  return mealType === 'dinner' ? 'day' : mealType;
};

export const DeletedDayCard = ({
  day,
  restoring,
  disabled,
  onRestore,
  onPickDifferent,
  onAddCustomMeal,
}: DeletedDayCardProps) => (
  <Paper
    radius="lg"
    withBorder
    p="xl"
    h="100%"
    style={{
      borderStyle: 'dashed',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 220,
    }}
  >
    <Stack gap={4} align="center">
      <Text fw={600} c="dimmed">
        {day.plannedDay}
      </Text>
      <Text size="sm" c="dimmed" ta="center">
        {day.recipeName
          ? `${day.recipeName} was removed`
          : `This ${removedNoun(day)} was removed`}
      </Text>
      <Group gap="xs" mt={6}>
        <Button
          size="xs"
          variant="light"
          leftSection={<IconArrowBackUp size={14} />}
          loading={restoring}
          disabled={disabled}
          onClick={onRestore}
        >
          {`Restore ${removedNoun(day)}`}
        </Button>
        {day.source !== 'custom_meal' && (
          <Button
            size="xs"
            variant="subtle"
            disabled={disabled || restoring}
            onClick={onPickDifferent}
          >
            Pick a different meal
          </Button>
        )}
        <Button
          size="xs"
          variant="subtle"
          disabled={disabled || restoring}
          onClick={onAddCustomMeal}
        >
          Pencil something in
        </Button>
      </Group>
    </Stack>
  </Paper>
);
